import type { AdminToolCall } from "../hooks/useAdminToolCalls";

// Pure status + sub-tool derivations for the tool-logs table, factored out of
// the page so they're unit-testable without a React renderer and shared by both
// the admin Tool Logs tab and the per-instance dashboard panel. The className
// coupling lives in the table component; here we only return the label.

export type SubToolEntry = {
  slug: string;
  ok: boolean;
  // IP-314: set when this sub-tool was rejected locally (unconnected toolkit).
  unconnected?: boolean;
};

// Partition a multi-execute row's sub-tools into success / genuine-failure /
// unconnected counts. `failed` excludes unconnected items (non-billable, never a
// failure) and is clamped >= 0. Null for non-multi-execute rows. Single source
// so the status pill and the count cell can never disagree (IP-314).
export type SubToolBreakdown = {
  count: number;
  success: number;
  failed: number;
  unconnected: number;
};

export type ToolCallStatusLabel = "ok" | "error" | "mixed" | "skipped";

export const subToolBreakdown = (row: AdminToolCall): SubToolBreakdown | null => {
  if (row.subToolCount == null) return null;
  const success = row.subToolSuccessCount ?? 0;
  const unconnected = row.subToolUnconnectedCount ?? 0;
  const failed = Math.max(0, row.subToolCount - success - unconnected);
  return { count: row.subToolCount, success, failed, unconnected };
};

// Status displayed in the table can differ from row.status. row.status is the
// transport-level outcome (did the MCP call return without throwing); for
// COMPOSIO_MULTI_EXECUTE_TOOL we also surface "mixed" when the batch came back
// but some sub-tools inside it failed. Pure UI derivation; the DB stays honest
// about the underlying transport outcome.
export const toolCallStatusDecision = (row: AdminToolCall): ToolCallStatusLabel => {
  const b = subToolBreakdown(row);
  // A batch in which EVERY sub-tool was rejected locally (unconnected) reads as
  // "skipped", not "error" — nothing was billed and nothing genuinely failed.
  // Checked before the transport-error branch because such a batch returns
  // isError.
  if (b && b.count > 0 && b.unconnected === b.count) return "skipped";
  if (row.status === "error") return "error";
  // Genuine failures only (unconnected items surface as the neutral pill/count,
  // never as a failure): 0 succeeded -> error; some succeeded -> mixed; no
  // genuine failures -> falls through to ok.
  if (b && b.failed > 0) return b.success === 0 ? "error" : "mixed";
  return "ok";
};

// V41: parse the sub_tools JSONB text into a typed array. Drops anything that
// doesn't match the {slug, ok} shape so a malformed row never crashes the page.
// The optional `unconnected` flag (IP-314) rides along when present.
export const parseSubTools = (raw: string | null): SubToolEntry[] => {
  if (raw == null) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is SubToolEntry =>
        e != null &&
        typeof e === "object" &&
        typeof (e as { slug?: unknown }).slug === "string" &&
        typeof (e as { ok?: unknown }).ok === "boolean",
    );
  } catch {
    return [];
  }
};

// JSON.parse the raw JSONB text returned by the backend; fall back to the raw
// string if it isn't valid JSON (shouldn't happen for well-formed rows).
export const prettyArgs = (raw: string | null): string => {
  if (raw == null) return "(no args)";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};
